import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Star, Sparkles } from 'lucide-react';

import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useWords } from '../hooks/useWords';
import { getDifficultyLabel, getDifficultyColor } from '../utils/difficultyUtils';
import type { WordWithProgress } from '../types';

const WordCard = ({ word }: { word: WordWithProgress }) => {
    const difficultyColor = getDifficultyColor(word.difficulty_level);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-lg overflow-hidden transform hover:-translate-y-2 transition-transform duration-300 flex flex-col h-full"
        >
            <div className={`p-5 bg-gradient-to-br ${difficultyColor} text-white`}>
                <h3 className="text-2xl font-bold truncate">{word.word}</h3>
                <p className="text-sm opacity-90 font-medium">{word.category}</p>
            </div>
            <div className="p-5 flex-grow flex flex-col">
                <p className="text-gray-600 text-base mb-4 flex-grow h-20 overflow-hidden">{word.definition}</p>
                <div className="flex justify-between items-center mb-4">
                    <span className={`px-4 py-1.5 text-sm font-bold text-white rounded-full ${difficultyColor}`}>
                        {getDifficultyLabel(word.difficulty_level)}
                    </span>
                    {word.user_progress ? (
                        <div className="text-right">
                            <p className="text-md font-bold text-gray-800 flex items-center">
                                <Star className="w-4 h-4 mr-1 text-yellow-500" /> {word.user_progress.best_score}%
                            </p>
                            <p className="text-xs text-gray-500">Attempts: {word.user_progress.attempts}</p>
                        </div>
                    ) : (
                        <div className="text-sm text-gray-500 italic">Not practiced</div>
                    )}
                </div>
                <Link to={`/word/${word.id}`} className="mt-auto">
                    <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-xl text-lg transition-all duration-300 shadow-md hover:shadow-lg">
                        Let's Practice!
                    </Button>
                </Link>
            </div>
        </motion.div>
    );
};

export default function WordsPage() {
    const { data: words, isLoading, error } = useWords();
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('alphabetical');
    const [filterBy, setFilterBy] = useState('all');
    const [selectedCategory, setSelectedCategory] = useState('all');

    const categories = useMemo(() => {
        if (!words) return [];
        const uniqueCategories = [...new Set(words.map(word => word.category))];
        return ['all', ...uniqueCategories.sort()];
    }, [words]);

    const filteredAndSortedWords = useMemo(() => {
        if (!words) return [];

        let filtered = [...words];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(word =>
                word.word.toLowerCase().includes(term) ||
                word.definition?.toLowerCase().includes(term) ||
                word.category?.toLowerCase().includes(term)
            );
        }

        if (selectedCategory !== 'all') {
            filtered = filtered.filter(word => word.category === selectedCategory);
        }

        if (filterBy !== 'all') {
            filtered = filtered.filter(word => {
                const hasProgress = !!(word as any).user_progress;
                switch (filterBy) {
                    case 'completed': return hasProgress && (word as any).user_progress!.is_completed;
                    case 'in-progress': return hasProgress && !(word as any).user_progress!.is_completed;
                    case 'not-started': return !hasProgress;
                    default: return true;
                }
            });
        }

        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'alphabetical': return a.word.localeCompare(b.word);
                case 'difficulty': return a.difficulty_level - b.difficulty_level;
                case 'progress':
                    const aScore = (a as any).user_progress?.best_score || 0;
                    const bScore = (b as any).user_progress?.best_score || 0;
                    return bScore - aScore;
                case 'recent':
                    const aDate = (a as any).user_progress?.last_practiced_at || '1970-01-01';
                    const bDate = (b as any).user_progress?.last_practiced_at || '1970-01-01';
                    return new Date(bDate).getTime() - new Date(aDate).getTime();
                default: return 0;
            }
        });

        return filtered;
    }, [words, searchTerm, sortBy, filterBy, selectedCategory]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-blue-50 flex items-center justify-center">
                <div className="text-center">
                    <LoadingSpinner size="lg" className="mb-4" />
                    <p className="text-lg text-blue-700 font-semibold">Loading Awesome Words...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-red-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-xl">
                    <h2 className="text-2xl font-bold text-red-700 mb-2">Oops! Something went wrong.</h2>
                    <p className="text-gray-600 mb-6">We couldn't load the words. Please try again!</p>
                    <Button onClick={() => window.location.reload()} variant="destructive">Try Again</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-100 to-yellow-50">
            <div className="container mx-auto px-4 py-10">
                <header className="text-center mb-12">
                    <motion.h1
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
                        className="text-6xl font-extrabold text-blue-700 tracking-tight drop-shadow-md"
                    >
                        Word Explorer
                    </motion.h1>
                    <p className="text-xl text-gray-600 mt-3">Discover new words and become a pronunciation star!</p>
                </header>

                <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                    className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-10 ring-1 ring-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-center">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search for words..."
                                className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <select
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white"
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                        >
                            <option value="alphabetical">Sort: Alphabetical</option>
                            <option value="difficulty">Sort: Difficulty</option>
                            <option value="progress">Sort: Your Progress</option>
                            <option value="recent">Sort: Recently Practiced</option>
                        </select>
                        <select
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white"
                            value={filterBy}
                            onChange={e => setFilterBy(e.target.value)}
                        >
                            <option value="all">Show: All Words</option>
                            <option value="completed">Show: Completed</option>
                            <option value="in-progress">Show: In Progress</option>
                            <option value="not-started">Show: Not Started</option>
                        </select>
                        <select
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white"
                            value={selectedCategory}
                            onChange={e => setSelectedCategory(e.target.value)}
                        >
                            {categories.map(cat => (
                                <option key={cat} value={cat || ''}>{cat === 'all' ? 'All Categories' : cat}</option>
                            ))}
                        </select>
                    </div>
                </motion.div>

                {filteredAndSortedWords && filteredAndSortedWords.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                        {filteredAndSortedWords.map(word => (
                            <WordCard key={word.id} word={word} />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white/50 rounded-2xl shadow-lg">
                        <Sparkles className="mx-auto h-24 w-24 text-yellow-500 mb-6" />
                        <h3 className="text-2xl font-bold text-gray-800">No words found!</h3>
                        <p className="text-gray-600 mt-2">Try adjusting your filters or search terms.</p>
                    </div>
                )}
            </div>
        </div>
    );
}